import React, { useMemo, useRef } from 'react';
import useResizeObserver from 'Hooks/useResizeObserver';
import ReactECharts from 'echarts-for-react';
import get from 'lodash/get';
import {
  CHART_COLORS,
  INSIGHT_CHART_FONT_CHAR_WIDTH,
} from 'utils/constants/dashboard';
import { formatChartsTooltip } from '../helpers';
import { VerticalBarChartProps } from '../chartBuilder';
import {
  CHART_EMPTY_STATE_COLORS,
  CHART_STYLING,
} from '../constants/chartColors';
import { verticalBarEmptyState } from '../constants/chartEmptyStates';
import { InsightDrilldownSelectedData } from '../../../types/store/normalized/insights/insightFilterMetadata';

const GenericVerticalBarChart: React.FC<VerticalBarChartProps> = ({
  chartData,
  onDrillDown,
  xAxisFieldName,
  yAxisFieldNames,
  lineFieldNames,
  lineLegendNames,
  legendNames,
  stacks,
  drillDownKeys,
  minInterval,
  lineAxisName,
  barAxisName,
}) => {
  const containerRef = useRef<any>();
  const { height, width } = useResizeObserver(containerRef);

  const onEvents = useMemo(
    () => ({
      click: (item: {
        seriesType?: string;
        dataIndex?: number;
        seriesIndex?: number;
      }) => {
        let data: InsightDrilldownSelectedData | undefined;

        if (
          xAxisFieldName &&
          item.dataIndex !== undefined &&
          chartData.length > 0
        ) {
          const chartItem = chartData[item.dataIndex];
          const fieldValue = chartItem[xAxisFieldName];

          if (fieldValue) {
            data = {
              filters: { fieldName: xAxisFieldName, fieldValue },
              chartData: chartItem,
            };
          }
        }

        if (onDrillDown && item.seriesType === 'bar') {
          let xAxisDrillKey = get(
            drillDownKeys,
            'categoryAxis.0',
            xAxisFieldName
          ) as string;
          const totalSeries =
            yAxisFieldNames.length + (lineFieldNames?.length || 0);

          let yAxisDrillKey = undefined;
          if (totalSeries > 1) {
            yAxisDrillKey = get(
              drillDownKeys,
              'valueAxis.' + get(item, 'seriesIndex', 0),
              get(yAxisFieldNames, get(item, 'seriesIndex', 0))
            ) as string;
          }

          if (
            yAxisDrillKey &&
            (drillDownKeys?.valueAxis?.includes(yAxisDrillKey) ||
              drillDownKeys?.categoryAxis?.includes(xAxisDrillKey))
          ) {
            onDrillDown(yAxisDrillKey, data);
          } else {
            onDrillDown(undefined, data);
          }
        }
      },
    }),
    [
      chartData,
      onDrillDown,
      drillDownKeys,
      xAxisFieldName,
      yAxisFieldNames,
      lineFieldNames?.length,
    ]
  );

  const options = useMemo(() => {
    // Handle empty data case
    if (chartData.length === 0) {
      return verticalBarEmptyState();
    }

    let yAxisCharCount = Math.max(
      ...chartData.map((i) =>
        Math.max(
          ...yAxisFieldNames.map((j) => get(i, j, '').toLocaleString().length)
        )
      )
    );

    let lineAxisCharCount =
      lineAxisName && lineFieldNames
        ? Math.max(
            ...chartData.map((i) =>
              Math.max(
                ...lineFieldNames.map(
                  (j) => get(i, j, '').toLocaleString().length
                )
              )
            )
          )
        : 0;

    let yAxisWidth = Math.min(
      120,
      yAxisCharCount * INSIGHT_CHART_FONT_CHAR_WIDTH
    );
    let lineAxisWidth = Math.min(
      120,
      lineAxisCharCount * INSIGHT_CHART_FONT_CHAR_WIDTH
    );
    let barAxisWidth = barAxisName ? 40 : 0;

    let remainingWidth =
      width -
      yAxisWidth -
      lineAxisWidth -
      barAxisWidth -
      (lineAxisWidth ? 20 : 0);

    let nonStackedBarCount = yAxisFieldNames.reduce((a, _c, i) => {
      if (stacks?.length && stacks[i] === undefined) return a + 1;
      return a;
    }, 0);

    const barsPerGroup = stacks?.length
      ? new Set(stacks).size + nonStackedBarCount
      : yAxisFieldNames.length;
    const barGap = 4;
    const barSize = 24;
    const groupGap = 16;
    const groupSize =
      groupGap + barSize * barsPerGroup + barGap * (barsPerGroup - 1);

    const legendHeight =
      yAxisFieldNames.length + (lineFieldNames?.length || 0) > 1 ? 24 : 0;

    let numOfBarGroups = Math.min(
      chartData.length,
      Math.floor(remainingWidth / groupSize)
    ); // It should be >= 1.
    let expandedGroupSize = Math.floor(remainingWidth / numOfBarGroups);
    let expandedBarSize = Math.floor(
      (expandedGroupSize - groupGap - barGap * (barsPerGroup - 1)) /
        barsPerGroup
    );

    const dataZoomOptions = [];
    if (numOfBarGroups < chartData.length) {
      dataZoomOptions.push({
        type: 'slider',
        startValue: 0,
        endValue: Math.max(0, numOfBarGroups - 1),
        height: 0,
        bottom: legendHeight + 12,
        zoomLock: true,
        showDetail: false,
        moveHandleSize: 12,
        moveHandleStyle: {
          color: CHART_EMPTY_STATE_COLORS.DATA_ZOOM_HANDLE,
          opacity: CHART_STYLING.FULL_OPACITY,
        },
      });
      dataZoomOptions.push({
        type: 'inside',
        orient: 'horizontal',
        startValue: 0,
        endValue: Math.max(0, numOfBarGroups - 1),
        zoomLock: false,
        zoomOnMouseWheel: false,
        moveOnMouseWheel: true,
        moveOnMouseMove: true,
      });
    }

    const series: unknown[] = yAxisFieldNames.map((i, idx) => ({
      type: 'bar',
      data: chartData.map((j) => get(j, i)),
      name: legendNames.length >= idx ? legendNames[idx] : undefined,
      barWidth: expandedBarSize,
      barCategoryGap: groupGap,
      barGap: `${(barGap / expandedBarSize) * 100}%`,
      stack: get(stacks, idx),
      itemStyle: get(stacks, idx)
        ? {
            borderWidth: 1,
            borderColor: CHART_EMPTY_STATE_COLORS.BORDER_WHITE,
          }
        : undefined,
    }));

    if (lineFieldNames && lineFieldNames.length) {
      series.push(
        ...lineFieldNames.map((i, idx) => ({
          type: 'line',
          data: chartData.map((j) => get(j, i)),
          name: get(lineLegendNames, idx),
          showSymbol: false,
          yAxisIndex: lineAxisWidth ? 1 : 0,
        }))
      );
    }

    const yAxis: unknown[] = [
      {
        type: 'value',
        position: 'left',
        name: barAxisName,
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: barAxisName ? 32 : 0,
        minInterval: minInterval || 0,
        splitLine: {
          show: false, // Hide grid lines for clarity
        },
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        axisLabel: {
          align: 'right',
          //width: yAxisWidth,
          overflow: 'truncate',
          margin: 0,
        },
      },
    ];

    if (lineAxisWidth) {
      yAxis.push({
        type: 'value',
        name: lineAxisName,
        position: 'right',
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: lineAxisWidth + 16,
        minInterval: minInterval || 0,
        splitLine: {
          show: false, // Hide grid lines for clarity
        },
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
          onZero: false,
        },
        axisLabel: {
          align: 'left',
          //width: lineAxisWidth,
          overflow: 'truncate',
          margin: 0,
        },
      });
    }

    return {
      dataZoom: dataZoomOptions,
      grid: {
        show: false,
        containLabel: true,
        top: 8,
        right: lineAxisWidth ? 24 : 0,
        bottom:
          numOfBarGroups < chartData.length ? legendHeight + 20 : legendHeight,
        left: barAxisWidth,
      },
      xAxis: {
        type: 'category',
        data: chartData.map((i) => get(i, xAxisFieldName)),
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        axisLabel: {
          interval: 0,
          hideOverlap: false,
          width: expandedGroupSize - groupGap,
          overflow: 'truncate',
          margin: 4,
        },
      },
      yAxis: yAxis,
      series: series,
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
        formatter: formatChartsTooltip,
      },
      legend: {
        show: series.length > 1,
        type: 'scroll',
        orient: 'horizontal',
        bottom: 0,
        left: 0,
        textStyle: {
          overflow: 'truncate',
        },
        data: [
          ...legendNames.map((i) => ({
            name: i,
            icon: 'circle',
          })),
          ...(lineLegendNames || []).map((i) => ({
            name: i,
          })),
        ],
      },
      textStyle: {
        fontFamily: 'inherit',
      },
      color: CHART_COLORS,
    };
  }, [
    chartData,
    legendNames,
    lineAxisName,
    lineFieldNames,
    lineLegendNames,
    minInterval,
    stacks,
    width,
    xAxisFieldName,
    yAxisFieldNames,
    barAxisName,
  ]);

  return (
    <div className='h-full w-full' ref={containerRef}>
      {width > 0 && (
        <ReactECharts
          key={`${width}-${height}`}
          opts={{ renderer: 'svg' }}
          option={options}
          style={{ height: height, width: width }}
          onEvents={onEvents}
        />
      )}
    </div>
  );
};

export default GenericVerticalBarChart;
